import React from "react";
import reactMixin from "react-mixin";
import ReactGA from "react-ga4";
import Intercom from "react-intercom";
import { Link } from "react-router";
import { AppBar, Snackbar } from "material-ui";
import withWidth, { SMALL, MEDIUM, LARGE } from "material-ui/utils/withWidth";
import { spacing as Spacing, colors as Colors } from "material-ui/styles";
import MuiThemeProvider from "material-ui/styles/MuiThemeProvider";
import getMuiTheme from "material-ui/styles/getMuiTheme";
import assign from "lodash/assign";
import isEqual from "lodash/isEqual";

import ErrorIcon from "material-ui/svg-icons/alert/error";

import FullWidthSection from "./full-width-section.jsx";
import AccountAppBarItem from "./partials/account-app-bar-item.jsx";
import GlobalSearch from "./partials/search/global-search.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import UserStore from "../stores/UserStore";
import EventBucketStore from "../stores/EventBucketStore";

import GlobalNotificationStore from "../stores/GlobalNotificationStore";
import GlobalNotificationActions from "../actions/GlobalNotificationActions";

import appUtil from "../utils/app";

const muiTheme = getMuiTheme({
  fontFamily: '"Inter", sans-serif',
  palette: {
    primary1Color: Colors.lightBlue700,
    primary2Color: Colors.grey500,
    primary3Color: Colors.lightBlue100,
    accent1Color: Colors.lightBlue500,
    accent2Color: Colors.grey300,
    accent3Color: Colors.grey100,
    textColor: Colors.grey900,
    canvasColor: Colors.white,
    borderColor: Colors.grey300,
    disabledColor: Colors.grey500
  },
  flatButton: {
    color: Colors.white,
    fontWeight: 600
  },
  button: {
    minWidth: 5,
    fontWeight: 600
  },
  raisedButton: {
    fontWeight: 600
  }
});

class App extends React.Component {
  constructor() {
    super();

    this.state = {
      hasNewVersion: false
    };

    this.checkVersion = this.checkVersion.bind(this);

    this._onLeftIconButtonTouchTap = this._onLeftIconButtonTouchTap.bind(this);
    this.welcome = this.welcome.bind(this);
  }

  static getStores() {
    return [UserStore, EventBucketStore, GlobalNotificationStore];
  }

  static getPropsFromStores() {
    let { isLoggedIn, data } = UserStore.getState();

    return {
      isLoggedIn: isLoggedIn,
      userData: data,
      eventBuckets: EventBucketStore.getEventBuckets(),
      globalNotification: GlobalNotificationStore.getState()
    };
  }

  static contextTypes = {
    router: React.PropTypes.object
  };

  static childContextTypes = {
    router: React.PropTypes.object,
    isLarge: React.PropTypes.bool,
    isMedium: React.PropTypes.bool,
    isSmall: React.PropTypes.bool
  };

  getChildContext() {
    return {
      router: this.context.router,
      isLarge: this.props.width === LARGE,
      isMedium: this.props.width === MEDIUM,
      isSmall: this.props.width === SMALL
    };
  }

  _updateHeap(userData) {
    let newData = {
      email: userData.email,
      name: userData.name,
      created_at: userData.created_at,
      company_id: userData.owner.id,
      company_name: userData.owner.organization
    };

    // Don't push if no data changed
    if (isEqual(this.lastHeap, newData)) {
      return;
    }

    this.lastHeap = newData;

    window.heap.identify(userData.id);
    window.heap.addUserProperties(newData);
  }

  componentWillMount() {
    // Handle redirecting based on login status
    const isRootOrLogin =
        ["/", "/login"].indexOf(this.props.location.pathname) !== -1,
      isUnauthenticatedRoute =
        [
          "/forgot",
          "/reset",
          "/activate",
          "/accept_creator_invite",
          "/accept_revenue_sharing_invite",
          "/sso"
        ].indexOf(this.props.location.pathname) !== -1;

    if (
      this.props.location.pathname === "/login" &&
      this.props.location.query.feedback
    ) {
      // fall through - canny login
    } else if (this.props.isLoggedIn) {
      if (isRootOrLogin) {
        this.context.router.replace("/welcome");
      } else {
        // fall through - continue to their request page
      }
    } else {
      if (!isUnauthenticatedRoute) {
        this.context.router.replace({
          pathname: "/login",
          query: isRootOrLogin ? {} : { next: this.props.location.pathname }
        });
      } else {
        // fall through - allow to forgot/reset flow
      }
    }

    if (
      process.env.NODE_ENV === "production" &&
      process.env.GOOGLE_ANALYTICS_ID
    ) {
      ReactGA.initialize(process.env.GOOGLE_ANALYTICS_ID);
      ReactGA.send("pageview");

      this.unlisten = this.context.router.listen(() => {
        ReactGA.send("pageview");
      });
    }
  }

  componentWillUnmount() {
    if (!this.unlisten) return;

    this.unlisten();
  }

  componentWillReceiveProps(props) {
    if (props.isLoggedIn && window.heap) {
      this._updateHeap(props.userData);
    }
  }

  checkVersion() {
    appUtil.checkIfLatestVersion().then(isLatest => {
      if (!isLatest) {
        this.setState({ hasNewVersion: true });
      }
    });
  }

  componentDidMount() {
    // Check for new version every 5 minutes.
    this.versionCheckInterval = setInterval(this.checkVersion, 1000 * 60 * 5);
    this.checkVersion();

    // Initialize changelog inject
    if (window.Canny) {
      window.Canny("initChangelog", {
        appID: "5ca03fa696cc92037d9ac47e",
        position: "bottom",
        align: "left"
      });
    }
  }

  getStyles() {
    return {
      canvas: {
        height: "100%",
        backgroundColor: muiTheme.palette.canvasColor,
        WebkitFontSmoothing: "antialiased",
        display: "flex",
        minHeight: "100vh",
        flexDirection: "column"
      },
      bar: {
        position: "fixed",
        flex: "none"
        //textAlign: 'center'
      },
      footer: {
        backgroundColor: Colors.grey900,
        textAlign: "center",
        color: Colors.white,
        flex: "none",
        zIndex: 1
      },
      img: {
        height: "80px"
      },
      content: {
        flex: "1 0 auto",
        padding: `${Spacing.desktopKeylineIncrement}px 0 0`,
        margin: "0 auto",
        width: "100%"
      },
      contentWhenMedium: {
        padding: "64px 20px",
        boxSizing: "border-box"
      },
      footerWhenMedium: {
        marginLeft: 256
      },
      notice: muiTheme.prepareStyles({
        borderRadius: 10,
        padding: "0.5em 1em",
        marginTop: 32,
        marginLeft: 20,
        marginRight: 20,
        marginBottom: -12,
        border: "1px solid #ffeb3b",
        backgroundColor: "#fff8bd",
        display: "flex",
        alignItems: "center"
      }),
      noticeWhenMedium: {
        marginLeft: 256 + 20
      },
      noticeIcon: {
        minWidth: 20,
        height: 20,
        verticalAlign: "-5px",
        marginRight: "1em",
        color: "#f9a825"
      },
      footerLink: {
        textDecoration: "none",
        color: Colors.white
      },
      eventiveLogo: {
        width: 75,
        marginLeft: 2
      }
    };
  }

  render() {
    let styles = this.getStyles(),
      isSmall = this.props.width === SMALL,
      title = (
        <span className={`eventive-title${isSmall ? "-small" : ""}`}>
          <span style={{ cursor: "pointer" }} onTouchTap={this.welcome}>
            {isSmall ? (
              <img
                src="https://storage.googleapis.com/eventive-co/EventiveMark.png"
                style={{ width: 24, height: 29, verticalAlign: "text-top" }}
              />
            ) : (
              "Eventive Dashboard"
            )}
          </span>
          {!isSmall ? (
            <span
              data-canny-changelog
              style={{
                fontSize: 12,
                marginLeft: 10,
                cursor: "pointer",
                verticalAlign: "middle",
                padding: "2px 8px",
                borderRadius: 5,
                border: `1px solid ${Colors.lightBlue500}`,
                backgroundColor: Colors.lightBlue600,
                fontWeight: "bold"
              }}
            >
              What's new
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                style={{
                  width: 14,
                  height: 14,
                  verticalAlign: -3,
                  marginLeft: 2,
                  marginRight: -5
                }}
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <style>{`
                #canny-changelog-iframe { position: fixed !important; }
                .Canny_BadgeContainer { top: -4px !important; right: -4px !important; }
              `}</style>
            </span>
          ) : null}
        </span>
      );

    // Intercom data
    let { userData, eventBuckets } = this.props,
      intercomData = userData && {
        email: userData.email,
        name: userData.name,
        user_id: userData.id,
        user_hash: userData.hash,
        created_at: userData.created_at,
        eventive_role: userData.role,
        company: {
          id: userData.owner.id,
          name: userData.owner.organization,
          type: userData.owner.type,
          eventive_organization_id: userData.owner.id
        }
      };
    if (intercomData && !UserStore.isGod()) {
      Object.assign(intercomData.company, {
        event_buckets: eventBuckets.length,
        events: eventBuckets
          .map(b => b.events.length)
          .reduce((a, b) => a + b, 0),
        pass_buckets: eventBuckets
          .map(b => b.pass_buckets.length)
          .reduce((a, b) => a + b, 0),
        coupons: eventBuckets
          .map(b => b.coupons.length)
          .reduce((a, b) => a + b, 0),
        venues: eventBuckets
          .map(b => b.venues.length)
          .reduce((a, b) => a + b, 0)
      });
    }

    let notice,
      noticeIcon,
      isDashboard = this.props.routes.some(r => r.name === "dashboard"),
      user = UserStore.getUser();

    if (
      user &&
      user.virtual_festival_plan_status === "active" &&
      user.virtual_festival_plan_payment_due_at
    ) {
      let isBillingHub = this.context.router.isActive({
        pathname: "/billing"
      });
      noticeIcon = <ErrorIcon style={styles.noticeIcon} />;
      notice = (
        <div>
          <strong>Renewal notice</strong>: Your Eventive Virtual annual
          subscription payment is due. Please{" "}
          {isBillingHub ? (
            "renew your subscription below"
          ) : (
            <Link to="/billing">renew your subscription</Link>
          )}{" "}
          to avoid losing access to Eventive Virtual.
        </div>
      );
    } else if (
      user &&
      user.needs_stripe &&
      user.type !== "content_creator" &&
      isDashboard
    ) {
      let isOrganization = this.context.router.isActive({
        pathname: "/organization"
      });
      noticeIcon = <ErrorIcon style={styles.noticeIcon} />;
      notice = (
        <div>
          <strong>Unable to accept payments</strong>: Until you{" "}
          {isOrganization ? (
            "connect a bank account below"
          ) : (
            <Link to="/organization">connect a bank account</Link>
          )}{" "}
          via Stripe, {user.organization} will not be able to accept credit card
          payments.{" "}
          <a
            target="_blank"
            href="https://help.eventive.org/getting-started/connect-your-bank-account"
          >
            Need&nbsp;help?
          </a>
        </div>
      );
    }

    return (
      <MuiThemeProvider muiTheme={muiTheme}>
        <div style={assign({}, styles.canvas)}>
          <Snackbar
            message="A new version of the Eventive Dashboard is available."
            action="update"
            open={this.state.hasNewVersion}
            style={{ left: 20, bottom: 20, right: "initial" }}
            onActionTouchTap={() => window.location.reload(true)}
            onRequestClose={() => {}}
          />

          <Snackbar
            autoHideDuration={5000}
            message={this.props.globalNotification.message}
            open={this.props.globalNotification.open}
            bodyStyle={{
              height: "auto",
              lineHeight: "24px",
              padding: "12px 24px",
              whiteSpace: "pre-line"
            }}
            onRequestClose={() => GlobalNotificationActions.close()}
          />

          <AppBar
            showMenuIconButton={false}
            title={title}
            iconStyleRight={{ marginTop: 13 }}
            iconElementRight={<AccountAppBarItem compact={isSmall} />}
            style={assign({}, styles.bar)}
            zDepth={2}
            className="no-print"
            id="eventive-app-bar"
          >
            {user &&
            user.type !== "content_creator" &&
            !this.context.router.isActive({
              pathname: "/god/lookup"
            }) ? (
              <GlobalSearch compact={isSmall} />
            ) : null}
          </AppBar>

          <div
            style={assign({}, styles.content, styles.contentWhenMedium)}
            className="print-reset-main"
          >
            {notice && (
              <div
                style={assign(
                  {},
                  styles.notice,
                  !isSmall && styles.noticeWhenMedium
                )}
              >
                {noticeIcon}
                {notice}
              </div>
            )}
            {this.props.children}
          </div>

          <FullWidthSection
            className="no-print"
            style={assign(
              { transition: "margin 0.2s ease-in-out" },
              styles.footer,
              !isSmall &&
                this.props.routes.some(r => r.name === "dashboard") &&
                !window.__eventiveSidebarCollapsed &&
                styles.footerWhenMedium
            )}
          >
            <div>
              <div>
                <a style={styles.footerLink} href="https://eventive.org">
                  Powered by{" "}
                  <img style={styles.eventiveLogo} src="/img/eventive.png" />
                </a>
              </div>
              <div>
                <a
                  style={styles.footerLink}
                  href="https://eventive.org/terms"
                  target="_blank"
                >
                  Terms
                </a>{" "}
                ·{" "}
                <a
                  style={styles.footerLink}
                  href="https://eventive.org/privacy"
                  target="_blank"
                >
                  Privacy
                </a>{" "}
                ·{" "}
                <a
                  style={styles.footerLink}
                  href="https://status.eventive.org/"
                  target="_blank"
                >
                  System Status
                </a>
              </div>
            </div>
          </FullWidthSection>

          <Intercom appID={process.env.INTERCOM_APP_ID} {...intercomData} />
        </div>
      </MuiThemeProvider>
    );
  }

  _onLeftIconButtonTouchTap() {
    this.refs.leftNav.toggle();
  }

  welcome() {
    this.context.router.push("/welcome");
  }
}

export default withWidth()(connectToStores(App));



// WEBPACK FOOTER //
// ./src/app/components/app.jsx